import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { formActionOutcomeReport } from './genieLeaseActions';
import type { GenieFormAction, GenieLeaseAction } from './genieLeaseActions';
import type { ChargeFrequency, NewRecurringChargeDto, RecurringChargeChangeDto } from '~/swagger/portal/Api';

export interface LeaseGenieChargePrefill {
  dueDate?: string;
  chartOfAccountId?: string;
  amount?: string;
  description?: string;
  billDate?: string;
}

export interface LeaseGenieDepositPrefill {
  dueDate?: string;
  billDate?: string;
  chartOfAccountId?: string;
  amount?: string;
  description?: string;
}

export interface LeaseGenieRecurringChargePrefill {
  amount?: string;
  chartOfAccountId?: string;
  description?: string;
  startFrom?: string;
  end?: string;
  chargeFrequency?: ChargeFrequency;
}

/** Prefill for the "set up recurring payment" form (amount is a number in that form). */
export interface LeaseGenieRecurringPaymentPrefill {
  amount?: number;
  startFrom?: string;
  end?: string;
  description?: string;
}

/** Prefill for the "add subsidy" form (amount is a string in that form). */
export interface LeaseGenieSubsidyPrefill {
  amount?: string;
  effectiveDate?: string;
  endDate?: string;
  description?: string;
}

/** Prefill for the shared "receive payment" form used by both the lease and deposit ledgers. */
export interface LeaseGenieOfflinePaymentPrefill {
  amount?: number;
  date?: string;
  description?: string;
  reference?: string;
}

/**
 * Prefill for the "create lease renewal" wizard. The AI works out the offer with the PM in chat,
 * then hands over the new term, changes to existing recurring charges, and any brand-new charges.
 * The wizard computes a default offer on open and layers these values over it.
 */
export interface LeaseGenieRenewalPrefill {
  newEndDate?: string;
  offerExpires?: string;
  recurringChargeChanges?: RecurringChargeChangeDto[];
  newRecurringCharges?: NewRecurringChargeDto[];
}

/**
 * Hand-off point between the lease-detail Genie chat and the lease's
 * existing create modals. `useLeaseGenieBridge` sets a prefill here and
 * navigates to the modal's route; the modal consumes it exactly once (via
 * `consume*`) so a PM's next normal, non-Genie open of the same modal
 * starts blank as usual.
 */
export const [LeaseGeniePrefillProvider, useLeaseGeniePrefill] = createMagicDoorContext('LeaseGeniePrefill', () => {
  const [chargePrefill, setChargePrefill] = createSignal<LeaseGenieChargePrefill>();
  const [depositPrefill, setDepositPrefill] = createSignal<LeaseGenieDepositPrefill>();
  const [recurringChargePrefill, setRecurringChargePrefill] = createSignal<LeaseGenieRecurringChargePrefill>();
  const [recurringPaymentPrefill, setRecurringPaymentPrefill] = createSignal<LeaseGenieRecurringPaymentPrefill>();
  const [subsidyPrefill, setSubsidyPrefill] = createSignal<LeaseGenieSubsidyPrefill>();
  const [leasePaymentPrefill, setLeasePaymentPrefill] = createSignal<LeaseGenieOfflinePaymentPrefill>();
  const [depositPaymentPrefill, setDepositPaymentPrefill] = createSignal<LeaseGenieOfflinePaymentPrefill>();
  const [renewalPrefill, setRenewalPrefill] = createSignal<LeaseGenieRenewalPrefill>();

  // The action awaiting the PM's approve/reject in the Genie confirmation modal (null = none open).
  const [pendingAction, setPendingAction] = createSignal<GenieLeaseAction>();

  // The chat panel registers its reporter here on mount. Held in a signal rather than passed down
  // so the confirmation modal and the create forms — none of which are children of the panel — can
  // report an outcome without threading a callback through the whole lease page.
  let actionReporter: ((text: string) => void) | undefined;

  // Outcomes raised while no panel is mounted. Some Genie forms (the renewal wizard) live on a
  // sibling route that replaces the lease page and its chat dock, so a save there completes with
  // nothing listening. Holding the text until a panel registers keeps that report alive; the panel
  // resumes the same conversation from session storage, so it still lands in the right thread.
  let bufferedReports: string[] = [];

  // A create action that navigated to a native form, held until the PM saves or leaves it.
  const [outstandingFormAction, setOutstandingFormAction] = createSignal<GenieFormAction>();

  const reportActionResult = (text: string) => {
    if (actionReporter == null) {
      bufferedReports.push(text);
      return;
    }
    actionReporter(text);
  };

  const registerActionReporter = (report: (text: string) => void) => {
    actionReporter = report;
    const buffered = bufferedReports;
    bufferedReports = [];
    for (const text of buffered) report(text);
    // Identity-guarded so a late disposer can only ever clear its own registration, never one a
    // newer panel has since installed.
    return () => {
      if (actionReporter === report) actionReporter = undefined;
    };
  };

  // Whether a pending action was rejected, failed, or had nothing to apply is known only to the
  // confirmation modal, so it registers how to report an abandoned one rather than the context
  // flattening every case to "rejected".
  let reportAbandonedAction: (() => void) | undefined;

  /**
   * Reports how a form-based action ended and clears the slot. Safe to call when nothing is
   * outstanding — a PM opening the same form themselves later must not report anything.
   */
  const resolveFormAction = (outcome: 'submitted' | 'dismissed') => {
    const action = outstandingFormAction();
    if (action == null) return;
    setOutstandingFormAction(undefined);
    reportActionResult(formActionOutcomeReport(action, outcome));
  };

  // Dock open-state lives here (not in LeaseDetails) so the "Ask Genie" trigger can sit inside the
  // lease-term banner (rendered deep in Overview) while the dock itself renders in LeaseDetails.
  const [genieOpen, setGenieOpen] = createSignal(false);

  const consumeChargePrefill = (): LeaseGenieChargePrefill | undefined => {
    const value = chargePrefill();
    if (value != null) setChargePrefill(undefined);
    return value;
  };

  const consumeDepositPrefill = (): LeaseGenieDepositPrefill | undefined => {
    const value = depositPrefill();
    if (value != null) setDepositPrefill(undefined);
    return value;
  };

  const consumeRecurringChargePrefill = (): LeaseGenieRecurringChargePrefill | undefined => {
    const value = recurringChargePrefill();
    if (value != null) setRecurringChargePrefill(undefined);
    return value;
  };

  const consumeRecurringPaymentPrefill = (): LeaseGenieRecurringPaymentPrefill | undefined => {
    const value = recurringPaymentPrefill();
    if (value != null) setRecurringPaymentPrefill(undefined);
    return value;
  };

  const consumeSubsidyPrefill = (): LeaseGenieSubsidyPrefill | undefined => {
    const value = subsidyPrefill();
    if (value != null) setSubsidyPrefill(undefined);
    return value;
  };

  const consumeLeasePaymentPrefill = (): LeaseGenieOfflinePaymentPrefill | undefined => {
    const value = leasePaymentPrefill();
    if (value != null) setLeasePaymentPrefill(undefined);
    return value;
  };

  const consumeDepositPaymentPrefill = (): LeaseGenieOfflinePaymentPrefill | undefined => {
    const value = depositPaymentPrefill();
    if (value != null) setDepositPaymentPrefill(undefined);
    return value;
  };

  const consumeRenewalPrefill = (): LeaseGenieRenewalPrefill | undefined => {
    const value = renewalPrefill();
    if (value != null) setRenewalPrefill(undefined);
    return value;
  };

  // A prefill is only cleared when its modal consumes it. If the model emits a
  // create-action but the PM closes the dock before the target modal mounts,
  // the value would linger and prefill the next non-Genie open of that modal on
  // this lease. Clearing on dock close drops those unconsumed prefills.
  const clearPrefills = () => {
    // Closing the chat abandons the confirmation modal with it, which is an outcome Genie needs —
    // report it before the state is dropped. An outstanding form action is deliberately left
    // alone: its modal lives on a route and stays open on screen, so the PM can still save it.
    if (pendingAction() != null) reportAbandonedAction?.();
    setChargePrefill(undefined);
    setDepositPrefill(undefined);
    setRecurringChargePrefill(undefined);
    setRecurringPaymentPrefill(undefined);
    setSubsidyPrefill(undefined);
    setLeasePaymentPrefill(undefined);
    setDepositPaymentPrefill(undefined);
    setRenewalPrefill(undefined);
    setPendingAction(undefined);
  };

  return {
    setChargePrefill,
    consumeChargePrefill,
    setDepositPrefill,
    consumeDepositPrefill,
    setRecurringChargePrefill,
    consumeRecurringChargePrefill,
    setRecurringPaymentPrefill,
    consumeRecurringPaymentPrefill,
    setSubsidyPrefill,
    consumeSubsidyPrefill,
    setLeasePaymentPrefill,
    consumeLeasePaymentPrefill,
    setDepositPaymentPrefill,
    consumeDepositPaymentPrefill,
    setRenewalPrefill,
    consumeRenewalPrefill,
    pendingAction,
    setPendingAction,
    clearPendingAction: () => setPendingAction(undefined),
    registerActionReporter,
    registerAbandonedActionReporter: (report: () => void) => {
      reportAbandonedAction = report;
    },
    reportActionResult,
    outstandingFormAction,
    setOutstandingFormAction,
    resolveFormAction,
    clearPrefills,
    genieOpen,
    setGenieOpen,
    openGenie: () => setGenieOpen(true),
  };
});
